import "Components/TitleBar/TitleBar.less";
import {
	selectSelectedLanguage, changeLanguageAction, setNavbarCollapsedAction, selectNavbarCollapsed
} from "Data/Redux/Slices/UserSettings";
import { logoutUserAction, selectCurrentUser, selectToken } from "Data/Redux/Slices/User";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
// eslint-disable-next-line @typescript-eslint/no-shadow
import type { MouseEvent } from "react";
import { Fragment, memo } from "react";
import { NavLink, useMatch } from "react-router-dom";
import { HoverIcon, LayeredIcon, Popover, PopoverTrigger } from "@clintonelec/react-storybook";
import { Languages } from "Data/Objects/Languages";
import { useMediaQuery } from "@mantine/hooks";
import { TitleBar as ClintonTitleBar } from "@clintonelec/react-storybook";
import ClintonLetterLogoSvg from "../../Images/Clinton_C_Logo_White.svg";
import ClintonLogoSvg from "../../Images/Clinton_Logo_White.svg";

const TitleBar = () => {
	const navCategory = useMatch("/setup/*");
	const smallScreen = useMediaQuery("(max-width: 768px)");
	const dispatch = useAppDispatch();
	const authToken = useAppSelector(selectToken);
	const currentUser = useAppSelector(selectCurrentUser);
	const currentLanguage = useAppSelector(selectSelectedLanguage);
	const changeLanguage = (selectedLanguage: Languages) => dispatch(changeLanguageAction(selectedLanguage));
	const setNavCollapsed = (newNavCollapsed: boolean) => dispatch(setNavbarCollapsedAction(newNavCollapsed));
	const navCollapsed = useAppSelector(selectNavbarCollapsed);
	const handleLogoutClick = () => dispatch(logoutUserAction());
	const handleToggleNavCollapsed = () => setNavCollapsed(!navCollapsed);
	const smallLogoClass = smallScreen ? "smallscreen-logo" : "";
	const logo = smallScreen ? ClintonLetterLogoSvg : ClintonLogoSvg;

	const handleChangeLanguage = (event: MouseEvent<HTMLLIElement>) => {
		changeLanguage(event.currentTarget.dataset.value as Languages);
	};

	const renderTitleBarControls = () => {
		if (!authToken || !currentUser) {
			return;
		}

		return (
			<Fragment>
				<ClintonTitleBar.Button>
					<NavLink to="/">
						<HoverIcon name="tv" />
					</NavLink>
				</ClintonTitleBar.Button>
				<ClintonTitleBar.Button>
					<NavLink to="/search">
						<HoverIcon name="clapperboard-play" />
					</NavLink>
				</ClintonTitleBar.Button>
				<ClintonTitleBar.Button>
					<NavLink to="/setup/camera/codec">
						<HoverIcon name="gear" />
					</NavLink>
				</ClintonTitleBar.Button>
			</Fragment>
		);
	};

	const renderLogoutButton = () => {
		if (!authToken || !currentUser) {
			return;
		}

		return (
			<ClintonTitleBar.Button onClick={ handleLogoutClick }>
				<LayeredIcon name="arrow-right-from-bracket" />
			</ClintonTitleBar.Button>
		);
	};

	const renderLanguageOption = (language: string) => {
		const className = currentLanguage === language ? "current-language" : "";

		return (
			<li className={ className } data-value={ language } key={ language } onClick={ handleChangeLanguage }>
				{ language }
			</li>
		);
	};

	const languagePopoverContent = (
		<ul className="language-list">
			{ Object.values(Languages).map(renderLanguageOption) }
		</ul>
	);

	const rightContent = (
		<Fragment>
			{ renderTitleBarControls() }
			<a href="https://support.clintonelectronics.com" rel="noreferrer" target="_blank">
				<ClintonTitleBar.Button>
					<LayeredIcon name="circle-question" />
				</ClintonTitleBar.Button>
			</a>
			<Popover content={ languagePopoverContent } trigger={ PopoverTrigger.CLICK }>
				<ClintonTitleBar.Button>
					<HoverIcon className="language-button" name="language" />
				</ClintonTitleBar.Button>
			</Popover>
			{ renderLogoutButton() }
		</Fragment>
	);

	return (
		<ClintonTitleBar>
			<ClintonTitleBar.Section className="left-section">
				<ClintonTitleBar.Collapse
					isCollapsible={ !!navCategory && smallScreen }
					onToggleCollapse={ handleToggleNavCollapsed }
				/>
				<ClintonTitleBar.Logo src={ logo } className={ smallLogoClass }/>
			</ClintonTitleBar.Section>
			<ClintonTitleBar.Section>
				{ rightContent }
			</ClintonTitleBar.Section>
		</ClintonTitleBar>
	);
};

export default memo(TitleBar);
